// src/pages/PurchaseExcelUploader.js
import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import LayoutUserHeader from '../components/LayoutUserHeader';
import LayoutAdminHeader from '../components/LayoutAdminHeader';
import * as XLSX from "xlsx";
import { useNavigate } from 'react-router-dom';
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

const PurchaseExcelUploader = () => {
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // ✅ Check gstNumber from localStorage
  useEffect(() => {
    const gstNumber = localStorage.getItem("gstNumber");
    setIsAdmin(String(gstNumber).trim() === "Admin");
  }, []);

  const parseExcelDate = (excelDate) => {
    if (!excelDate) return null;
    if (typeof excelDate === "number") {
      const parsed = XLSX.SSF.parse_date_code(excelDate);
      return new Date(parsed.y, parsed.m - 1, parsed.d);
    }
    return new Date(excelDate);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      const invoiceSheet = workbook.Sheets[workbook.SheetNames[0]];
      const productSheet = workbook.Sheets[workbook.SheetNames[1]];

      const invoices = XLSX.utils.sheet_to_json(invoiceSheet);
      const products = XLSX.utils.sheet_to_json(productSheet);

      const invoicesMap = {};

      invoices.forEach((row) => {
        if (!row.InvoiceNumber) return;

        invoicesMap[row.InvoiceNumber] = {
          InvoiceNumber: row.InvoiceNumber,
          InvoiceDate: parseExcelDate(row.InvoiceDate),
          StateId: row.StateId || "",
          StateCode: row.StateCode || "",
          GRNumber: row.GRNumber || "",
          TransportDate: row.TransportDate || "",
          PlaceOfSupply: row.PlaceOfSupply || "",
          VehicleNumber: row.VehicleNumber || "",
          TranspotationMode: row.TranspotationMode || "",
          TransporterName: row.TransporterName || "",
          CustomerName: row.CustomerName || "",
          CustomerAddress: row.CustomerAddress || "",
          CustomerStateId: row.CustomerStateId || "",
          CustomerStateCode: row.CustomerStateCode || "",
          CustomerGSTNumber: row.CustomerGSTNumber || "",
          GSTNumber: row.GSTNumber || "",
          SupplierType: row.SupplierType || "Registered",
          Products: [],
        };
      });

      products.forEach((row) => {
        const invoice = invoicesMap[row.InvoiceNumber];
        if (!invoice) return;

        invoice.Products.push({
          ProductId: row.ProductId || "",
          HSNCode: row.HSNCode || "",
          Quantity: String(row.Quantity || "0"),
          Unit: row.Unit || "",
          Rate: String(row.Rate || "0"),
          Amount: String(row.Amount || "0"),
          IGSTPercentage: String(row.IGSTPercentage || "0"),
          CGSTPercentage: String(row.CGSTPercentage || "0"),
          SGSTPercentage: String(row.SGSTPercentage || "0"),
          IGSTAmount: String(row.IGSTAmount || "0"),
          CGSTAmount: String(row.CGSTAmount || "0"),
          SGSTAmount: String(row.SGSTAmount || "0"),
          DiscountAmount: String(row.DiscountAmount || "0"),
          OtherCharges: String(row.OtherCharges || "0"),
          Cess: String(row.Cess || "0"),
        });
      });

 // NEW CALCULATION BLOCK
Object.values(invoicesMap).forEach((invoice) => {
  let total = 0;

  invoice.Products.forEach((p) => {
    const toNum = (v) => parseFloat(v || "0");

    const amount = toNum(p.Amount);
    const otherCharges = toNum(p.OtherCharges);
    const discount = toNum(p.DiscountAmount);
    const base = amount + otherCharges - discount;

    // ✅ Calculate GST on base (including OtherCharges)
    const igstPerc = toNum(p.IGSTPercentage);
    const cgstPerc = toNum(p.CGSTPercentage);
    const sgstPerc = toNum(p.SGSTPercentage);

    const igstAmount = (base * igstPerc) / 100;
    const cgstAmount = (base * cgstPerc) / 100;
    const sgstAmount = (base * sgstPerc) / 100;

    // ✅ Save calculated GST amounts (optional — for backend or debugging)
    p.IGSTAmount = igstAmount.toFixed(2);
    p.CGSTAmount = cgstAmount.toFixed(2);
    p.SGSTAmount = sgstAmount.toFixed(2);

    // ✅ Total per product (including GST, OtherCharges, Cess, and Discount)
    const productTotal = base + igstAmount + cgstAmount + sgstAmount + toNum(p.Cess);
    total += productTotal;
  });

  invoice.TotalAmount = total.toFixed(2);
});


      const formattedData = Object.values(invoicesMap).filter(
        (inv) => inv.Products.length > 0
      );

      if (!formattedData.length) {
        toast.error("No valid invoices found in Excel");
        return;
      }

      console.log(formattedData);

      const token = localStorage.getItem("token");
      await axios.post(`${BASE_URL}/PurchaseInvoice/BulkUpload`, formattedData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Purchase Invoices uploaded successfully");
      navigate('/GetPurchaseInvoice');
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to process file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isAdmin ? <LayoutAdminHeader /> : <LayoutUserHeader />}

      <Box p={2}>
        <ToastContainer />
        <Typography variant="h5" gutterBottom>
          Upload Purchase Invoices from Excel
        </Typography>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          disabled={loading}
        />
        {loading && <p>Processing file...</p>}
      </Box>
    </>
  );
};

export default PurchaseExcelUploader;
